import { validRegions } from "./DxfRegionSelector";
import api from "../../../data/api/mainApi";

export async function fetchLayouts() {
  const { data } = await api.get("/millings/dxf-layouts");
  if (!Array.isArray(data)) throw new Error("A szerver sablonlistája nem olvasható.");
  data.forEach(validateLayout);
  return data;
}
export async function saveLayout(layout) {
  const { data } = await api.post("/millings/dxf-layouts", validateLayout(layout));
  return validateLayout(data);
}
export async function deleteLayout(id) {
  await api.delete(`/millings/dxf-layouts/${encodeURIComponent(id)}`);
}

export const LAYOUT_KEY = "milling:dxf-layouts:v1";
const mm = { 1: 25.4, 4: 1, 5: 10, 6: 1000 };
const validBounds = b => Array.isArray(b) && b.length === 4 && b.every(Number.isFinite) && b[2] > b[0] && b[3] > b[1];
export function validateLayout(layout) {
  if (layout?.version !== 1 || typeof layout.id !== "string" || typeof layout.name !== "string" || !layout.name.trim() ||
      layout.name.length > 150 || !mm[layout.units] ||
      !Array.isArray(layout.origin) || layout.origin.length !== 2 || !layout.origin.every(Number.isFinite) ||
      !Array.isArray(layout.regions) || layout.regions.some(r =>
        !r || typeof r.name !== "string" || r.name.length > 150 ||
        (r.cutouts != null && (!Array.isArray(r.cutouts) || r.cutouts.length > 30 || r.cutouts.some(c => !c)))) ||
      !validRegions(layout.regions) || (layout.bounds != null && !validBounds(layout.bounds)) ||
      (layout.sourceType != null && !["image", "dxf"].includes(layout.sourceType)) ||
      (layout.sourceType === "image" && !validBounds(layout.bounds))) throw new Error("Érvénytelen darabolási sablon.");
  return layout;
}
export function makeLayout(name, regions, units, scene) {
  return validateLayout({ version: 1, id: crypto.randomUUID(), name: name.trim(), units,
    origin: scene.min.slice(0, 2),
    sourceType: "dxf", bounds: [scene.min[0], scene.min[1], scene.max[0], scene.max[1]],
    regions: regions.map(r => ({ name: r.name.trim(), rect: [...r.rect],
      cutouts: (r.cutouts || []).map(c => ({ rect: [...c.rect] })) })) });
}
export function applyLayout(layout, units, scene, align) {
  validateLayout(layout);
  if (layout.sourceType === "image") return fitLayout(layout, [scene.min[0], scene.min[1], scene.max[0], scene.max[1]], false);
  if (!mm[units]) throw new Error("Ismeretlen célmértékegység.");
  const scale = mm[layout.units] / mm[units];
  const offset = align ? scene.min.slice(0, 2).map((n, i) => n - layout.origin[i] * scale) : [0, 0];
  const transform = rect => rect.map((n, i) => n * scale + offset[i % 2]);
  return layout.regions.map(r => ({ id: crypto.randomUUID(), name: r.name, rect: transform(r.rect),
    cutouts: (r.cutouts || []).map(c => ({ id: crypto.randomUUID(), rect: transform(c.rect) })) }));
}
export function layoutBounds(layout) {
  validateLayout(layout);
  return layout.bounds || [Math.min(...layout.regions.map(r => r.rect[0])), Math.min(...layout.regions.map(r => r.rect[1])),
    Math.max(...layout.regions.map(r => r.rect[2])), Math.max(...layout.regions.map(r => r.rect[3]))];
}
function fitLayout(layout, target, imageTarget) {
  const source = layoutBounds(layout);
  if (!validBounds(target)) throw new Error("Érvénytelen illesztési terület.");
  const sx = (target[2] - target[0]) / (source[2] - source[0]);
  const sy = (target[3] - target[1]) / (source[3] - source[1]);
  const flip = (layout.sourceType === "image") !== imageTarget;
  const transform = r => {
    const x1 = target[0] + (r[0] - source[0]) * sx, x2 = target[0] + (r[2] - source[0]) * sx;
    const y1 = flip ? target[3] - (r[3] - source[1]) * sy : target[1] + (r[1] - source[1]) * sy;
    const y2 = flip ? target[3] - (r[1] - source[1]) * sy : target[1] + (r[3] - source[1]) * sy;
    return imageTarget ? [x1, y1, x2, y2].map(Math.round) : [x1, y1, x2, y2];
  };
  return layout.regions.map(r => ({ id: crypto.randomUUID(), name: r.name, rect: transform(r.rect),
    cutouts: (r.cutouts || []).map(c => ({ id: crypto.randomUUID(), rect: transform(c.rect) })) }));
}
export function applyImageLayout(layout, size, target = [0, 0, size.w, size.h]) {
  if (!validBounds(target) || target[0] < 0 || target[1] < 0 || target[2] > size.w || target[3] > size.h)
    throw new Error("Az illesztési területnek a képen belül kell lennie.");
  return fitLayout(layout, target, true);
}
export function makeImageLayout(name, regions, size) {
  const layout = makeLayout(name, regions, 4, { min: [0, 0], max: [size.w, size.h] });
  return { ...layout, sourceType: "image" };
}
export function readLayouts() {
  const layouts = JSON.parse(localStorage.getItem(LAYOUT_KEY) || "[]");
  if (!Array.isArray(layouts)) throw new Error("A mentett sablonok nem olvashatók.");
  layouts.forEach(validateLayout);
  return layouts;
}
export function writeLayouts(layouts) {
  layouts.forEach(validateLayout);
  localStorage.setItem(LAYOUT_KEY, JSON.stringify(layouts));
}
